import { EstateItem, Classification, EstateJsonData, EstateStageJson } from '@types';
import { EstateMetadata } from '../stores/estateStore';
import { BlocPlusEstate } from '../services/blocPlusApi';

export interface MergeResult {
  items: EstateItem[];
  skipped: number;
}

export function normalizeLotStatusForBlocPlus(status: string | undefined): string {
  // `future` is a stage release status in BlocPlus, not a valid lot status.
  // Lots in future stages remain `available`; the stage controls visibility and availability.
  if (status === 'future') return 'available';
  return status === 'under-offer' ? 'under offer' : status || 'available';
}

export function normalizeLotStatusForEstateMate(status: string | undefined): string {
  return status === 'under offer' ? 'under-offer' : status || 'available';
}

export function normalizeEstateStages(json: EstateJsonData): EstateStageJson[] {
  const lotCodes = new Set((json.lots || []).map((lot) => String(lot.code).trim()).filter(Boolean));
  if (json.stages?.length) {
    return json.stages.map((stage) => ({
      ...stage,
      name: String(stage.name).trim(),
      lots: Array.from(
        new Set(
          (stage.lots || []).map((code) => String(code).trim()).filter((code) => lotCodes.has(code))
        )
      ),
    }));
  }

  const legacyStage = json.stage?.trim();
  return legacyStage
    ? [
        {
          name: legacyStage,
          release_status: 'available',
          show_price_publicly: true,
          lots: Array.from(lotCodes),
        },
      ]
    : [];
}

export function mergeEstateStages(
  metadata: EstateMetadata[],
  items: EstateItem[]
): EstateStageJson[] {
  const retainedLotCodes = new Set(
    items.filter((item) => item.classification === 'lot').map((item) => item.code)
  );
  const stagesByName = new Map<string, EstateStageJson>();

  metadata
    .flatMap((entry) => entry.stages || [])
    .forEach((stage) => {
      const name = stage.name.trim();
      if (!name) return;
      const existing = stagesByName.get(name);
      const lots = new Set([...(existing?.lots || []), ...(stage.lots || [])].map(String));
      stagesByName.set(name, {
        ...existing,
        ...stage,
        name,
        lots: Array.from(lots).filter((code) => retainedLotCodes.has(code)),
      });
    });

  return Array.from(stagesByName.values());
}

export function estateItemsToJson(
  metadata: EstateMetadata | null,
  items: EstateItem[]
): EstateJsonData {
  const lots = items.filter((item) => item.classification === 'lot');
  const references = items.filter(
    (item) => item.classification !== 'lot' && item.classification !== 'deleted'
  );
  const stages = metadata?.stages?.length
    ? mergeEstateStages([metadata], items)
    : metadata?.stage.trim()
      ? [
          {
            name: metadata.stage.trim(),
            release_status: 'available' as const,
            show_price_publicly: true,
            lots: lots.map((lot) => lot.code),
          },
        ]
      : [];

  return {
    geoLocation: metadata?.geolocation || { lat: 0, lng: 0 },
    name: metadata?.name || '',
    address: metadata?.address || '',
    website: '',
    status: metadata?.status || 'draft',
    stages,
    lots: lots.map((lot) => ({
      code: lot.code,
      handleId: lot.handleid,
      landSize: lot.landsize,
      frontage: lot.frontage,
      price: lot.price,
      status: normalizeLotStatusForBlocPlus(lot.status),
      points: lot.points,
    })),
    references: references.map((reference) => ({
      name: reference.code || reference.classification,
      type: reference.classification,
      handleId: reference.handleid,
      points: reference.points,
    })),
  };
}

export function blocPlusToItems(estate: BlocPlusEstate): EstateItem[] {
  const lots: EstateItem[] = (estate.lots || []).map((lot) => ({
    code: lot.code,
    handleid: lot.handleId || lot.id,
    landsize: lot.landSize || 0,
    frontage: lot.frontage || 0,
    price: parseFloat(lot.price) || 0,
    status: normalizeLotStatusForEstateMate(lot.status?.code),
    points: lot.points || [],
    classification: 'lot' as Classification,
  }));

  const refs: EstateItem[] = (estate.references || []).map((ref, i) => ({
    code: ref.name,
    handleid: ref.handleId || `ref-${i}-${ref.name}`,
    landsize: 0,
    frontage: 0,
    price: 0,
    status: '',
    points: ref.points || [],
    classification: (ref.type?.code || 'road') as Classification,
  }));

  return [...lots, ...refs];
}

export function blocPlusToMetadata(estate: BlocPlusEstate): EstateMetadata {
  const stagesByName = new Map<string, EstateStageJson>();
  (estate.lots || []).forEach((lot) => {
    const stage = lot.stage;
    if (!stage?.name) return;
    const existing = stagesByName.get(stage.name);
    stagesByName.set(stage.name, {
      name: stage.name,
      release_date: stage.estimatedTitleDate,
      title_status: stage.titleStatus,
      release_status: stage.releaseStatus,
      show_price_publicly: stage.showPricePublicly,
      lots: [...(existing?.lots || []), lot.code],
    });
  });
  const stages = Array.from(stagesByName.values());
  return {
    planNumber: estate.name,
    name: estate.name,
    stage: stages.map((stage) => stage.name).join(', '),
    stages,
    address: '',
    status: estate.status?.code || 'active',
    geolocation: estate.geoLocation || { lat: -37.5, lng: 145.5 },
  };
}

export function jsonDataToItems(json: EstateJsonData): EstateItem[] {
  const lots: EstateItem[] = (json.lots || []).map((lot) => ({
    code: lot.code,
    handleid: lot.handleId || lot.handleid || lot.code,
    landsize: lot.landSize || lot.landsize || 0,
    frontage: lot.frontage,
    price: lot.price,
    status: normalizeLotStatusForEstateMate(lot.status),
    points: lot.points,
    classification: 'lot' as Classification,
  }));

  const refs: EstateItem[] = (json.references || []).map((ref, i) => ({
    code: ref.name,
    handleid: ref.handleId || `ref-${i}-${ref.name}`,
    landsize: 0,
    frontage: 0,
    price: 0,
    status: '',
    points: ref.points,
    classification: ref.type as Classification,
  }));

  return [...lots, ...refs];
}

export function jsonDataToMetadata(json: EstateJsonData): EstateMetadata {
  const geo = json.geoLocation || json.geolocation || { lat: 0, lng: 0 };
  const stages = normalizeEstateStages(json);
  return {
    planNumber: json.name,
    name: json.name,
    stage: stages.map((stage) => stage.name).join(', '),
    stages,
    address: json.address || '',
    status: json.status || 'draft',
    geolocation: geo,
  };
}

/**
 * Merge additions into base. First occurrence of a code wins — duplicates are skipped.
 */
export function mergeItems(base: EstateItem[], additions: EstateItem[][]): MergeResult {
  const existingCodes = new Set(base.map((i) => i.code));
  let skipped = 0;
  const merged = [...base];

  for (const addition of additions) {
    for (const item of addition) {
      if (existingCodes.has(item.code)) {
        skipped++;
      } else {
        merged.push(item);
        existingCodes.add(item.code);
      }
    }
  }

  return { items: merged, skipped };
}

export function lotCount(items: EstateItem[]): number {
  return items.filter((i) => i.classification === 'lot').length;
}

export function refCount(items: EstateItem[]): number {
  return items.filter((i) => i.classification !== 'lot' && i.classification !== 'deleted').length;
}
